import React, { useEffect, useState } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import {
    CButton, CCard, CCardBody, CCardHeader, CCardFooter,
    CForm, CFormSelect, CFormFeedback, CSpinner,
    CTable, CTableHead, CTableRow, CTableHeaderCell, CTableBody, CTableDataCell, CRow, CCol
} from '@coreui/react';

import { apiClient } from 'services/Api';
import { jwtDecode } from 'jwt-decode';
import TextEditor from "components/forms/TextEditor";
import ToastNotification from "components/globals/ToastNotification";
import { triggerAlert } from 'components/globals/AlertNotification';

const surveyFields = [
    ['q1', '1 - Os exames de saúde realizados no âmbito da Medicina do Trabalho são suficientes e adequados ao seu posto de trabalho?'],
    ['q2', '2 - A informação e formação que dispõe sobre os riscos a que está exposto e sobre as medidas de prevenção a adotar no seu posto de trabalho é suficiente e adequada?'],
    ['q3', '3 - A sinalização de segurança existente (perigo, emergência, obrigação, informação, etc) é suficiente e adequada?'],
    ['q4', '4 - Os locais de circulação (escadas, portas, pavimentos, corredores) apresentam as devidas condições de segurança?'],
    ['q5', '5 - Existem implementadas medidas de combate a incêndio, primeiros socorros e evacuação para responder a uma situação de emergência?'],
    ['q6', '6 - As medidas de combate a incêndio, primeiros socorros e evacuação para responder a uma situação de emergência são adequadas?'],
    ['q7', '7 - Está exposto a riscos especiais (amianto, espaços confinados, etc)?'],
    ['q8', '8 - Está exposto a agentes físicos (ruído, vibrações, radiações, iluminação inadequada, etc)?'],
    ['q9', '9 - Está exposto a agentes químicos (poeiras, gases, vapores, fumos, cheiros, etc)?'],
    ['q10', '10 - Está exposto a riscos ergonómicos (movimentação manual de cargas, sobre-esforços, movimentos repetitivos, posturas inadequadas, fadiga visual, etc)?'],
    ['q11', '11 - Está exposto a riscos biológicos (vírus, bactérias, fungos, etc)?'],
    ['q12', '12 - As medidas de prevenção e de proteção já colocadas em prática no seu posto de trabalho são adequadas?'],
    ['q13', '13 - Existem medidas de prevenção e de proteção que devam ser postas em prática no seu posto de trabalho?'],
    ['q14', '14 - Tem à sua disposição os equipamentos de proteção individual de acordo com os riscos a que está exposto no seu posto de trabalho?'],
];

const SstSurvey = () => {
    const { id } = useParams();
    const isNew = !id || id === 'new';
    const navigate = useNavigate();
    const location = useLocation();

    const [formData, setFormData] = useState(null);
    const [fieldErrors, setFieldErrors] = useState({});
    const [toast, setToast] = useState(null);
    const [loading, setLoading] = useState(false);

    const token = localStorage.getItem("access_token");
    const decodedToken = token ? jwtDecode(token) : {};
    const is_director = decodedToken?.is_director;

    const buildEmptyFormData = () => {
        const init = {};
        surveyFields.forEach(([key]) => { init[key] = ''; }); // keep as '' | 'true' | 'false'
        init.obs = '';
        return init;
    };

    const fetchSurvey = async () => {
        console.log(id)
        if (isNew) {
            // New record: initialize blank form
            setFormData({ concluded: false, ...buildEmptyFormData() });
            return;
        }
        try {
            const response = await apiClient.get(`/sst-surveys/${id}/`);
            const data = response.data;

            setFormData(data);
        } catch (error) {
            triggerAlert('Inquérito SST não foi encontrado', 'error');
            navigate('/inqueritos-sst');
        }
    };

    useEffect(() => {
        fetchSurvey();
    }, [id, location.pathname]);

    const handleInputChange = (e) => {
        const { name, value } = e.target; // value is '' | 'true' | 'false'
        setFormData((prev) => ({ ...prev, [name]: value }));

        // Clear only the specific field error for q1..q14
        setFieldErrors((prev) => {
            const updated = { ...prev };
            delete updated[name];
            return updated;
        });
    };

    const handleObsChange = (value) => {
        // obs never uses fieldErrors
        setFormData((prev) => ({ ...prev, obs: value }));
    };

    // Convert to API payload:
    // - q1..q14: 'true'/'false' -> boolean, '' omitted
    // - obs: keep string (omit if empty/whitespace)
    const buildPayload = () => {
        const payload = {};
        surveyFields.forEach(([key]) => {
            const v = formData[key];
            if (v === 'true') payload[key] = true;
            else if (v === 'false') payload[key] = false;
            // omit if ''
        });
        if (formData.obs && formData.obs.trim() !== '') {
            payload.obs = formData.obs;
        }
        return payload;
    };

    const handleSubmit = async (e) => {
        e?.preventDefault?.();
        setLoading(true);
        try {
            const payload = buildPayload();

            if (isNew) {
                await apiClient.post('/sst-surveys/', payload);
                triggerAlert('Inquérito criado com sucesso.', 'success');
            } else {
                await apiClient.put(`/sst-surveys/${id}/`, payload);
                triggerAlert('Inquérito submetido com sucesso.', 'success');
            }

            navigate('/inqueritos-sst');
        } catch (error) {
            const errors = error.response?.data;
            if (errors && typeof errors === 'object') {
                // Keep only keys that belong to q1..q14
                const filtered = Object.fromEntries(
                    Object.entries(errors).filter(([k]) => surveyFields.some(([key]) => key === k))
                );
                setFieldErrors(filtered);

                // Scroll to first invalid control
                setTimeout(() => {
                    const firstInvalid = document.querySelector('select.is-invalid');
                    if (firstInvalid) {
                        firstInvalid.scrollIntoView({ behavior: 'smooth', block: 'center' });
                        firstInvalid.focus();
                    }
                }, 50);
            } else {
                setFieldErrors({});
            }

            setToast({
                title: 'Inquérito SST',
                message: isNew ? 'Erro ao criar o inquérito.' : 'Erro ao submeter o inquérito.',
                type: 'error',
            });
            console.error('Error submitting survey:', error);
        } finally {
            setLoading(false);
        }
    };

    if (formData === null) {
        return (
            <div className="container mt-4">
                <h2>Loading...</h2>
            </div>
        );
    }

    const isConcluded = !!formData.concluded;

    return (
        <div className="container mt-4">
            {/* Toast container (like the example) */}
            <ToastNotification toast={toast} setToast={setToast} />

            <CCard>
                <CCardHeader>
                    <h4>Segurança e Saúde no Trabalho Consulta aos Trabalhadores</h4>
                </CCardHeader>

                <CForm onSubmit={handleSubmit}>
                    <CCardBody>
                        <CRow>
                            <CCol className="text-center mb-4">
                                <p>O empregador deve consultar por escrito e, pelo menos, uma vez por ano, os trabalhadores em matéria de Segurança e Saúde no Trabalho (SST) (artigo 18º da Lei 102/2009 alterada pela Lei 3/2014). Por outro lado, também constitui obrigação do trabalhador cooperar ativamente na empresa para melhoria da sistema de SST (artigo 17º da Lei 102/2009). No seguimento das leis anteriormente referidas e com o objetivo da elaboração de dados estatísticos, a Stream Consulting realizou o seguinte inquérito destinado aos colaboradores.</p>
                            </CCol>
                        </CRow>

                        {isConcluded && (
                            <CRow>
                                <CCol className="mb-4">
                                    <strong>Colaborador: </strong>{formData.user_name}
                                </CCol>
                            </CRow>
                        )}

                        <CTable responsive hover>
                            <CTableBody>
                                {surveyFields.map(([key, question]) => {
                                    const selectId = `survey-${key}`;
                                    const invalid = !!fieldErrors[key];
                                    return (
                                        <CTableRow key={key}>
                                            <CTableDataCell className="col-10">
                                                <label htmlFor={selectId}>{question}</label>
                                            </CTableDataCell>
                                            <CTableDataCell className="col-2">
                                                <CFormSelect
                                                    id={selectId}
                                                    name={key}
                                                    value={formData[key] ?? ''}
                                                    onChange={handleInputChange}
                                                    disabled={isConcluded}
                                                    invalid={invalid}
                                                >
                                                    <option value="">Selecione...</option>
                                                    <option value="true">Sim</option>
                                                    <option value="false">Não</option>
                                                </CFormSelect>
                                                {invalid && (
                                                    <CFormFeedback invalid>
                                                        {Array.isArray(fieldErrors[key]) ? fieldErrors[key][0] : String(fieldErrors[key])}
                                                    </CFormFeedback>
                                                )}
                                            </CTableDataCell>
                                        </CTableRow>
                                    );
                                })}
                            </CTableBody>
                        </CTable>

                        {!isConcluded ? (
                            <TextEditor
                                label="Observações"
                                value={formData.obs ?? ''}
                                onChange={handleObsChange}
                                className="mb-3"
                            />
                        ) : (
                            <div className="mb-3">
                                <label><strong>Observações</strong></label>
                                <div
                                    className="border rounded p-2"
                                    dangerouslySetInnerHTML={{ __html: formData.obs || "<em>Sem observações.</em>" }}
                                />
                            </div>
                        )}
                    </CCardBody>

                    <CCardFooter className="d-flex justify-content-end gap-2">
                        <CButton color="danger" className="text-white" type="button" onClick={() => navigate("/inqueritos-sst")}>
                            Cancelar
                        </CButton>
                        {!isConcluded && (
                            <CButton color="success" className="text-white" type="submit" disabled={loading}>
                                {loading ? <CSpinner size="sm" /> : 'Submeter'}
                            </CButton>
                        )}
                    </CCardFooter>
                </CForm>
            </CCard>
        </div>
    );
};

export default SstSurvey;
